import { FC, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Box, Popover, Typography, useMediaQuery } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import { useQuery } from '@tanstack/react-query';
import { HOME_PAGE_URL, queryKeys } from '@/modules/_core/constants';
import { eventsService, usersService } from '@/modules/_core/services';
import {
	calcDefaultEndDate,
	DEFAULT_TIME_ZONE,
	EventFilter,
	formatEventTimeRange,
	getEventPriceRangeString,
	makeEventPageLink,
	priceFormatter,
} from '@sweatpals/shared';
import { EventClickArg, EventContentArg, EventHoveringArg, EventInput, EventSourceFuncArg } from '@fullcalendar/core';
import { EventCalendarStylesWrapper } from './embed-event-calendar-styles.component';
import { formatInTimeZone } from 'date-fns-tz';
import { Button, CalendarIcon, ClockIcon, MoneyBagIcon, PlaceIcon2, Showable } from '@sweatpals/react-ui-kit';
import type { EventType } from '@/modules/_core/types/event-types';
import { endOfMonth, isSameDay, startOfMonth, sub } from 'date-fns';

const mapEventDtoToEventInput = (event: EventType): EventInput => ({
	id: `${event.id}-${new Date(event.instance).toISOString()}`,
	title: event.name,
	start: event.instance,
	end: event.endDate ?? calcDefaultEndDate(event.instance),
	allDay: false,
	extendedProps: {
		// event,
		url: `${HOME_PAGE_URL}/${makeEventPageLink({
			eventAlias: event.alias,
			shortLocalInstance: event.shortLocalInstance,
		})}`,
		alias: event.alias,
		shortLocalInstance: event.shortLocalInstance,
		hostColor: event.author?.primaryColor,
		dateString: formatEventTimeRange(event.instance, event.endDate, {
			startDateFormat: 'EEEE MM/dd h:mmaa',
			endDateFormat: 'EEEE MM/dd h:mmaa',
			timeFormat: 'h:mmaa',
			separator: '-',
			timezone: event.tzid,
		}),
		addressString: event.isOnlineEvent ? 'Online event' : event.addressName,
		prisesString: event.isPaid
			? event.isDonationBased
				? `Donation${event.minimalDonationAmount ? ` (${priceFormatter(event.minimalDonationAmount)})` : ''}`
				: getEventPriceRangeString(event.prices)
			: 'Free',
		salesStartString: event.additionalData?.ticketSalesStartUtcTimeIso
			? `Tickets go live on ${formatInTimeZone(
					new Date(event.additionalData?.ticketSalesStartUtcTimeIso),
					event.tzid || DEFAULT_TIME_ZONE,
					'MM/dd at h:mmaa zzz'
				)}`
			: undefined,
		timeZone: event.tzid || DEFAULT_TIME_ZONE,
	},
});

const getEventContentId = (eventId: string) => `${eventId}-content`;

enum CalendarViews {
	dayGridMonth = 'dayGridMonth',
	listMonth = 'listMonth',
}

export interface EmbedEventCalendarPageProps {
	entityAlias: string;
	primaryColorHex?: string;
	popupBackgroundColorHex?: string;
	headerFontColorHex?: string;
	fontFamily?: string;
	showInfoPopup?: boolean;
	showLargePopup?: boolean;
}

export const CommunityEventCalendar: FC<EmbedEventCalendarPageProps> = (props) => {
	const {
		entityAlias: communityUsername,
		fontFamily,
		primaryColorHex,
		popupBackgroundColorHex,
		headerFontColorHex,
		showInfoPopup,
		showLargePopup = !showInfoPopup,
	} = props;

	const calendarApiRef = useRef<FullCalendar>(null);

	const isMobileScreen = useMediaQuery('(max-width: 900px)');

	const currentView = isMobileScreen ? CalendarViews.listMonth : CalendarViews.dayGridMonth;
	const isListMonthView = currentView === CalendarViews.listMonth;

	useEffect(() => {
		if (calendarApiRef.current) {
			calendarApiRef.current.getApi().changeView(currentView);
		}
	}, [currentView]);

	const [currentCalendarEvents, setCurrentCalendarEvents] = useState<EventInput[]>([]);
	const [isEventListLoading, setIsEventListLoading] = useState(false);

	const [hoverEventId, setHoverEventId] = useState<string | null>(null);
	const [popupAnchorEl, setPopupAnchorEl] = useState<null | HTMLElement>(null);

	const popupOpen = Boolean(popupAnchorEl);

	const handleOpenInfoPopup = useCallback((info: EventHoveringArg) => {
		setPopupAnchorEl(info.el);
		setHoverEventId(info.event.id);
	}, []);

	const handleClosePopup = useCallback(() => {
		setPopupAnchorEl(null);
		setTimeout(() => setHoverEventId(null), 300);
	}, []);

	const { data: hostId, isLoading: isHostIdLoading } = useQuery(
		queryKeys.hostRelated(communityUsername, 'get-id'),
		async () => usersService.findPublicIdByUsername(communityUsername)
	);

	const { data: hostData, isLoading: isHostDataLoading } = useQuery(queryKeys.hostRelated(hostId), async () =>
		usersService.getUserByIdPublic(hostId)
	);

	const hostIds = useMemo(() => [hostId].filter(Boolean), [hostId]);

	const fetchEvents = useCallback(
		async (fetchInfo: EventSourceFuncArg): Promise<EventInput[]> => {
			if (!hostIds.length) {
				return [];
			}

			try {
				setIsEventListLoading(true);

				const newEventFilter: EventFilter = {
					creatorsIds: hostIds,
					withUnverifiedEvents: true,
					periodFrom: fetchInfo?.start ? fetchInfo.start : startOfMonth(new Date()),
					periodTo: fetchInfo?.end ? fetchInfo.end : endOfMonth(new Date()),
				};

				const response = await eventsService.getPublicEventsList({
					...newEventFilter,
				});

				const eventInputs = (response?.data || []).map(mapEventDtoToEventInput);

				setCurrentCalendarEvents(eventInputs);

				return eventInputs;
			} catch (error) {
				console.error('Error fetching events:', error);
			} finally {
				setIsEventListLoading(false);
			}
		},
		[hostIds]
	);

	const hoverEventData = useMemo(
		() => currentCalendarEvents.find((e) => e.id === hoverEventId),
		[currentCalendarEvents, hoverEventId]
	);

	const openEventUrl = useCallback((event: EventInput) => {
		if (event?.extendedProps?.url) {
			window.open(event.extendedProps.url, '_blank');
		}
	}, []);

	const openLargePopup = useCallback((event: EventInput) => {
		if (!event) return;

		// search for checkout modal or if none open link in new tab

		console.debug('waiting for modal to open');
		let isModalOpened = false;

		window.addEventListener('message', (event) => {
			if (!event.data?.action) return;

			if (event.data.action === `modalOpened-eventDetails-${communityUsername}`) {
				isModalOpened = true;
			}
		});

		console.debug('requesting modal to open');
		// notify modal widget to open modal
		window.parent.postMessage(
			{
				action: `openEventDetails-${communityUsername}`,
				alias: event.extendedProps.alias,
				shortLocalInstance: event.extendedProps.shortLocalInstance,
			},
			'*'
		);

		setTimeout(() => {
			// if modal is not opened after timeout, open link in new tab
			if (!isModalOpened) {
				console.debug('modal not opened, opening link in new tab');

				openEventUrl(event);
			}
		}, 2000);
	}, []);

	const isLoading = isEventListLoading || isHostIdLoading || isHostDataLoading;

	const handleEventClick = useCallback(
		(eventClickInfo: EventClickArg) => {
			eventClickInfo.jsEvent?.preventDefault();

			if (isLoading) {
				return;
			}

			if (showLargePopup) {
				openLargePopup(eventClickInfo.event);
			} else if (showInfoPopup) {
				handleOpenInfoPopup(eventClickInfo);
			} else {
				openEventUrl(eventClickInfo.event);
			}
		},
		[showLargePopup, showInfoPopup, handleOpenInfoPopup, communityUsername, openEventUrl, isLoading]
	);

	const scrollTodayRef = useRef<boolean>(false);

	useEffect(() => {
		if (currentCalendarEvents?.length && !scrollTodayRef.current) {
			const calendarApi = calendarApiRef.current?.getApi();

			(window as any).calendarApi = calendarApi;

			if (calendarApi?.view?.type === CalendarViews.listMonth) {
				const currentDate = new Date();
				const firstEventDate = new Date(currentCalendarEvents[0].start as Date);

				// if current year and month is opened
				if (
					currentDate.getUTCFullYear() === firstEventDate.getUTCFullYear() &&
					currentDate.getUTCMonth() === firstEventDate.getUTCMonth()
				) {
					// scroll to current date
					const nearestEvent = currentCalendarEvents.find(
						(event) =>
							isSameDay(new Date(event.start as Date), currentDate) || // today
							new Date(event.start as Date).getTime() > currentDate.getTime() || // nearest future event
							sub(currentDate, { days: 3 }) < new Date(event.start as Date) // nearest past event
					);

					const nearestEventDate = nearestEvent ? new Date(nearestEvent.start as Date) : null;

					if (nearestEventDate) {
						window.setTimeout(() => {
							const scroller = document.querySelector(`.fc-scroller`);
							const eventElement = document.getElementById(`${nearestEvent.id}-event`);
							if (scroller && eventElement) {
								const top = eventElement.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 80;
								scroller.scrollTo({
									top: top <= 0 ? 0 : top,
									behavior: 'smooth',
								});
							}
						}, 2000);
					}

					scrollTodayRef.current = true;
				}
			}
		}

		if (!isListMonthView) {
			scrollTodayRef.current = false;
		}
	}, [currentCalendarEvents, isListMonthView]);

	return (
		<EventCalendarStylesWrapper
			isMobileView={isMobileScreen}
			isLoading={isLoading}
			hostData={hostData}
			primaryColorHex={primaryColorHex}
			popupBackgroundColorHex={popupBackgroundColorHex}
			headerFontColorHex={headerFontColorHex}
			fontFamily={fontFamily}
		>
			<FullCalendar
				ref={calendarApiRef}
				plugins={[dayGridPlugin, listPlugin]}
				initialView={currentView}
				titleFormat={{ year: 'numeric', month: 'long' }}
				views={{
					dayGrid: {},
					list: {},
				}}
				weekends={true}
				fixedWeekCount={true} // 6 weeks always
				events={fetchEvents}
				lazyFetching={true}
				dayMaxEvents={3}
				headerToolbar={{
					left: isListMonthView ? 'title' : 'prev,next today',
					center: isListMonthView ? 'empty' : 'title',
					right: isListMonthView ? 'today prev,next' : 'empty',
				}}
				eventContent={(e) => renderEventContent(e, isLoading)}
				eventClick={handleEventClick} // show event details popoup, currently opens url param
				// eventMouseEnter={handleOpenPopup}
				// eventMouseLeave={handleClosePopup}
			/>

			<Popover
				id={getEventContentId(hoverEventId)}
				open={popupOpen}
				onClose={handleClosePopup}
				anchorEl={popupAnchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				slotProps={{
					paper: {
						sx: {
							backgroundColor: 'var(--fc-popup-bg-color)',
							borderTopLeftRadius: '12px',
							borderTopRightRadius: '12px',
						},
					},
				}}
			>
				<Box
					sx={{
						py: '24px',
						px: '12px',
						width: 'auto',
						minWidth: '250px',
						display: 'flex',
						flexDirection: 'column',
						gap: '18px',
						color: 'var(--fc-font-color)',
						fontSize: 'var(--fc-font-size)',
						fontFamily: 'var(--fc-font-family)',
					}}
				>
					<Typography
						sx={{
							fontSize: '1.13em',
							lineHeight: '1.4',
							fontWeight: '700',
							color: 'inherit',
							fontFamily: 'inherit',
							textDecoration: 'underline',
						}}
					>
						{hoverEventData?.title}
					</Typography>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
						}}
					>
						<CalendarIcon />
						<Typography
							sx={{
								fontSize: '0.95em',
								lineHeight: '1.4',
								fontWeight: '500',
								color: 'inherit',
								fontFamily: 'inherit',
								opacity: 0.9,
							}}
						>
							{hoverEventData?.extendedProps?.dateString}
						</Typography>
					</Box>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
						}}
					>
						<PlaceIcon2 />
						<Typography
							sx={{
								fontSize: '0.95em',
								lineHeight: '1.4',
								fontWeight: '500',
								color: 'inherit',
								fontFamily: 'inherit',
								opacity: 0.9,
							}}
						>
							{hoverEventData?.extendedProps?.addressString}
						</Typography>
					</Box>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
						}}
					>
						<MoneyBagIcon />
						<Typography
							sx={{
								fontSize: '0.95em',
								lineHeight: '1.4',
								fontWeight: '500',
								color: 'inherit',
								fontFamily: 'inherit',
								opacity: 0.9,
							}}
						>
							{hoverEventData?.extendedProps?.prisesString}
						</Typography>
					</Box>
					<Showable show={hoverEventData?.extendedProps?.salesStartDate}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px',
							}}
						>
							<ClockIcon />
							<Typography
								sx={{
									fontSize: '0.95em',
									lineHeight: '1.4',
									fontWeight: '500',
									color: 'inherit',
									fontFamily: 'inherit',
									opacity: 0.9,
								}}
							>
								{hoverEventData?.extendedProps?.salesStartString}
							</Typography>
						</Box>
					</Showable>

					<Button
						strictColor
						onClick={() => openLargePopup(hoverEventData)}
						sx={{
							py: '6px',
							px: '20px',
							alignSelf: 'center',
							fontFamily: 'inherit',
							fontWeight: '600',
							textTransform: 'none',
							color: 'var(--fc-button-text-color)',
							backgroundColor: 'var(--fc-button-bg-color)',
							border: '1px solid',
							borderColor: 'var(--fc-button-text-color)',

							'&:hover': {
								color: 'var(--fc-button-text-color)',
								backgroundColor: 'var(--fc-button-hover-bg-color)',
								borderColor: 'var(--fc-button-text-color)',
							},
						}}
					>
						Go to event
					</Button>
				</Box>
			</Popover>
		</EventCalendarStylesWrapper>
	);
};

function renderEventContent(eventInfo: EventContentArg, isLoading?: boolean) {
	const id = getEventContentId(eventInfo.event.id);

	const isListView = eventInfo.view.type === CalendarViews.listMonth;

	return (
		<Box
			aria-describedby={id}
			component="span"
			id={`${eventInfo.event.id}-event`}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				px: '4px',
				color: 'var(--fc-primary-color)',
				fontSize: 'var(--fc-font-size)',
			}}
		>
			{isListView ? null : (
				<Box
					component="span"
					sx={{
						backgroundColor: eventInfo.event.extendedProps.hostColor || 'var(--fc-primary-color)',
						borderRadius: '50%',
						width: '10px',
						height: '10px',
						flexShrink: 0,
					}}
				/>
			)}

			<Box
				component="span"
				sx={{
					fontSize: '0.87em',
					overflow: isListView ? 'hidden' : 'initial',
					textOverflow: isListView ? 'ellipsis' : 'initial',
				}}
			>
				{isListView ? null : (
					<Box component="span" sx={{ fontWeight: '500' }}>
						{formatInTimeZone(eventInfo.event.start, eventInfo.event.extendedProps.timeZone, 'haaa')}
					</Box>
				)}{' '}
				<Box
					component="span"
					sx={{
						fontWeight: '600',
						lineHeight: isListView ? '1.2em' : 'initial',
						// wrap on white spaces
						whiteSpace: isListView ? 'nowrap' : 'normal',
						wordBreak: 'initial',
					}}
				>
					{eventInfo.event.title}
				</Box>
			</Box>
		</Box>
	);
}
